import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import api from 'src/api/BlancoClient';
import { NewspaperResponse, NewspapersResponse } from 'src/api/response/newspaper';
import { formatDateTime } from 'src/lib/dateFormatter';
import { extractInvalidChars } from 'src/lib/message-validator';
import { PersistentData, PersistentUserNewspaperState } from 'src/lib/PersistentData';
import { persistUserNewspaperState } from 'src/lib/storage';
import { Issue } from 'src/models/newspaper/Issue';
import { NewspaperArticle } from 'src/models/newspaper/NewspaperArticle';
import { ReportError } from 'src/models/report/ReportError';
import { ReportUserPayload } from 'src/models/report/ReportPayload';
import { RootState } from '.';
import { actions as userActions, getFreshToken, UserState } from './user';
import { checkErrorType } from './utils/helper';
import { RequestState } from './utils/RequestState';

export type NewspaperErrorType = 'loading-failed' | 'unstable-network' | 'unknown';

interface HomeContents {
  latest?: NewspaperResponse;
  issues: Issue[];
  isNewspaperDeleted?: boolean;
}

export interface NewspaperState {
  userNewspaperStates?: PersistentData['userNewspaperStates'];
  homeContents?: RequestState<HomeContents, NewspaperErrorType>;
  displayedNewspaper?: RequestState<NewspaperResponse, NewspaperErrorType>;
  patchNewspaperState?: RequestState<Record<string, unknown>, NewspaperErrorType>;
  noticeClosed?: boolean;
  reportStatus?: RequestState<Record<string, unknown>, ReportError>;
  deleteStatus?: RequestState<Record<string, unknown>, NewspaperErrorType>;
}

interface UpdateMainArticlePayload {
  findKey: string;
  indexes: number[]; // 更新したいmainArticleIndexes
}

interface DeletePlayerPayload {
  findKey: string;
  contentId: string;
}

const initialState: NewspaperState = {};

const slices = createSlice({
  name: 'newspaper',
  initialState,
  reducers: {
    persistentDataRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.userNewspaperStates = payload.userNewspaperStates;
    },
    getHomeContents: state => {
      state.homeContents = { state: 'pending' };
    },
    getHomeContentsSucceeded: (state, { payload }: PayloadAction<HomeContents>) => {
      state.homeContents = { state: 'ok', result: payload };
    },
    getHomeContentsFailed: (state, { payload }: PayloadAction<NewspaperErrorType>) => {
      state.homeContents = { state: 'error', error: payload };
    },
    getDisplayedNewspaper: (state, _: PayloadAction<{ findKey: string }>) => {
      state.displayedNewspaper = { state: 'pending' };
    },
    getDisplayedNewspaperSucceeded: (state, { payload }: PayloadAction<NewspaperResponse>) => {
      state.displayedNewspaper = { state: 'ok', result: payload };
    },
    getDisplayedNewspaperFailed: (state, { payload }: PayloadAction<NewspaperErrorType>) => {
      state.displayedNewspaper = { state: 'error', error: payload };
    },
    updateMainArticle: (state, _: PayloadAction<UpdateMainArticlePayload>) => {
      state.patchNewspaperState = { state: 'pending' };
    },
    updateMainArticleSucceeded: (state, { payload }: PayloadAction<UpdateMainArticlePayload>) => {
      state.patchNewspaperState = { state: 'ok', result: {} };
      const { findKey, indexes } = payload;
      // mainIndexesを更新する
      if (
        state.homeContents?.state === 'ok' &&
        state.homeContents.result.latest?.findKey === findKey
      ) {
        state.homeContents.result.latest.body.mainArticleIndexes = indexes;
      }
      if (
        state.displayedNewspaper?.state === 'ok' &&
        state.displayedNewspaper.result.findKey === findKey
      ) {
        state.displayedNewspaper.result.body.mainArticleIndexes = indexes;
      }
    },
    updateMainArticleFailed: (state, { payload }: PayloadAction<NewspaperErrorType>) => {
      state.patchNewspaperState = { state: 'error', error: payload };
    },
    resetUpdateMainArticle: state => {
      state.patchNewspaperState = undefined;
    },
    clearDisplayedNewspaper: state => {
      state.displayedNewspaper = undefined;
    },
    noticeClosed: _ => {
      // do nothing
    },
    persistentDataUpdated: (
      state,
      { payload }: PayloadAction<{ userId: string; data: PersistentUserNewspaperState }>
    ) => {
      const { userId, data } = payload;
      const current = state.userNewspaperStates || {};
      current[userId] = data;
      state.userNewspaperStates = current;
      state.noticeClosed = true;
    },
    resetDeleteStatus: state => {
      state.deleteStatus = undefined;
    },
    deletePlayer: (state, _: PayloadAction<DeletePlayerPayload>) => {
      state.deleteStatus = { state: 'pending' };
    },
    deletePlayerSucceeded: (state, { payload }: PayloadAction<DeletePlayerPayload>) => {
      state.deleteStatus = { state: 'ok', result: {} };
      const { contentId, findKey } = payload;
      if (
        state.homeContents?.state === 'ok' &&
        state.homeContents.result.latest?.findKey === findKey
      ) {
        const latestIssue = state.homeContents.result.latest;
        if (latestIssue) {
          const latestArticles = latestIssue.body.articles;
          const filteredArticles = getFilteredArticles(latestArticles, contentId);
          latestIssue.body.articles = filteredArticles;
          state.homeContents.result.latest = latestIssue;
        }
      } else if (
        state.displayedNewspaper?.state === 'ok' &&
        state.displayedNewspaper.result.findKey === findKey
      ) {
        const articles = state.displayedNewspaper.result.body.articles;
        const filteredArticles = getFilteredArticles(articles, contentId);
        state.displayedNewspaper.result.body.articles = filteredArticles;
      }
    },
    deletePlayerFailed: (state, { payload }: PayloadAction<NewspaperErrorType>) => {
      state.deleteStatus = { state: 'error', error: payload };
    },
    reportPlayer: (state, _: PayloadAction<ReportUserPayload>) => {
      state.reportStatus = { state: 'pending' };
    },
    reportPlayerSucceeded: (state, { payload }: PayloadAction<string>) => {
      state.reportStatus = { state: 'ok', result: {} };
    },
    reportPlayerFailed: (state, { payload }: PayloadAction<ReportError>) => {
      state.reportStatus = { state: 'error', error: payload };
    },
    resetReportStatus: state => {
      state.reportStatus = undefined;
    },
    reset: state => {
      state.homeContents = undefined;
      state.displayedNewspaper = undefined;
      state.patchNewspaperState = undefined;
      state.noticeClosed = undefined;
    }
  }
});

export default slices.reducer;

const {
  persistentDataRestored,
  getHomeContents,
  getDisplayedNewspaper,
  updateMainArticle,
  resetUpdateMainArticle,
  clearDisplayedNewspaper,
  noticeClosed,
  resetDeleteStatus,
  deletePlayer,
  resetReportStatus,
  reportPlayer,
  reset,
  ...rest
} = slices.actions;

export const actions = {
  persistentDataRestored,
  getHomeContents,
  getDisplayedNewspaper,
  updateMainArticle,
  resetUpdateMainArticle,
  clearDisplayedNewspaper,
  noticeClosed,
  resetDeleteStatus,
  deletePlayer,
  resetReportStatus,
  reportPlayer,
  reset
};

const {
  getHomeContentsSucceeded,
  getHomeContentsFailed,
  getDisplayedNewspaperSucceeded,
  getDisplayedNewspaperFailed,
  updateMainArticleSucceeded,
  updateMainArticleFailed,
  persistentDataUpdated,
  deletePlayerSucceeded,
  deletePlayerFailed,
  reportPlayerSucceeded,
  reportPlayerFailed
} = rest;

// Saga

export function* newspaperSaga() {
  yield takeLatest(getHomeContents.type, getHomeContentsSaga);
  yield takeLatest(getDisplayedNewspaper.type, getDisplayedNewspaperSaga);
  yield takeLatest(updateMainArticle.type, updateMainArticleSaga);
  yield takeLatest(noticeClosed.type, noticeClosedSaga);
  yield takeLatest(deletePlayer.type, deletePlayerSage);
  yield takeLatest(reportPlayer.type, reportPlayerSaga);
}

function* getHomeContentsSaga() {
  const { language }: UserState = yield select((state: RootState) => state.user);
  try {
    const baid: string = yield call(getFreshToken);
    if (!language) {
      throw new Error();
    }

    const unixTime = Math.floor(Date.now() / 1000);
    const formattedDateTimeString = formatDateTime(unixTime);

    const [latest, newspapersRes]: [NewspaperResponse, NewspapersResponse] = yield all([
      call(getNewspaperLatest, formattedDateTimeString, language, baid),
      call(api.getNewspapers, baid)
    ]);

    yield put(
      getHomeContentsSucceeded({
        latest,
        issues: newspapersRes.newspapers,
        isNewspaperDeleted: newspapersRes.isNewspaperDeleted
      })
    );
  } catch (error) {
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server':
        yield put(userActions.causeServerError(error.response.data?.code));
        break;
      case 'client':
        yield put(getHomeContentsFailed('unknown'));
        break;
      case 'unstable-network':
        yield put(getHomeContentsFailed('unstable-network'));
        break;
      default:
        yield put(getHomeContentsFailed('unknown'));
    }
  }
}

function* getDisplayedNewspaperSaga(action: {
  type: typeof getDisplayedNewspaper.type;
  payload: UpdateMainArticlePayload;
}) {
  const { findKey } = action.payload;
  const { language }: UserState = yield select((state: RootState) => state.user);
  const unixTime = Math.floor(Date.now() / 1000);
  const formattedDateTimeString = formatDateTime(unixTime);

  try {
    const baid: string = yield call(getFreshToken);
    if (!language) {
      throw new Error();
    }
    const res: NewspaperResponse = yield call(
      api.getNewspaper,
      findKey,
      formattedDateTimeString,
      language,
      baid
    );
    yield put(getDisplayedNewspaperSucceeded(res));
  } catch (error) {
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server':
        yield put(userActions.causeServerError(error.response.data?.code));
        break;
      case 'client':
        yield put(getDisplayedNewspaperFailed('unknown'));
        break;
      case 'unstable-network':
        yield put(getDisplayedNewspaperFailed('unstable-network'));
        break;
      default:
        yield put(getDisplayedNewspaperFailed('unknown'));
    }
  }
}

function* updateMainArticleSaga(action: {
  type: typeof getDisplayedNewspaper.type;
  payload: UpdateMainArticlePayload;
}) {
  const { findKey, indexes } = action.payload;
  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.patchNewspaper, findKey, indexes, baid);
    yield put(updateMainArticleSucceeded(action.payload));
  } catch (error) {
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server':
        yield put(userActions.causeServerError(error.response.data?.code));
        break;
      case 'client':
        yield put(updateMainArticleFailed('unknown'));
        break;
      case 'unstable-network':
        yield put(updateMainArticleFailed('unstable-network'));
        break;
      default:
        yield put(updateMainArticleFailed('unknown'));
    }
  }
}

function* noticeClosedSaga() {
  const { selectedUserId: userId }: UserState = yield select((state: RootState) => state.user);
  if (!userId) {
    throw new Error();
  }
  const timestamp = Math.floor(new Date().getTime() / 1000);
  const data = { latestUpdateTimestamp: timestamp };
  yield call(persistUserNewspaperState, userId, data);
  yield put(persistentDataUpdated({ userId, data }));
}

function* deletePlayerSage(action: {
  type: typeof deletePlayer.type;
  payload: DeletePlayerPayload;
}) {
  const { findKey, contentId } = action.payload;

  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.deleteNewspaperContent, findKey, contentId, baid);

    yield put(deletePlayerSucceeded(action.payload));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'client': {
        if (error.response.data?.code) {
          const { code } = error.response.data;
          if (code === '3005') {
            yield put(deletePlayerSucceeded(action.payload));
          } else {
            yield put(deletePlayerFailed('unknown'));
          }
        }
        break;
      }
      case 'unstable-network': {
        yield put(deletePlayerFailed('unstable-network'));
        break;
      }
      default:
        yield put(deletePlayerFailed('unknown'));
    }
  }
}

function* reportPlayerSaga(action: { type: typeof reportPlayer.type; payload: ReportUserPayload }) {
  const { type, language, category, reason, ugc } = action.payload;

  if (reason) {
    const invalidChars = extractInvalidChars(reason);
    if (invalidChars) {
      yield put(
        reportPlayerFailed({
          type: 'invalid-char',
          char: invalidChars
        })
      );
      return;
    }
  }
  try {
    const baid: string = yield call(getFreshToken);
    const { contentId, digest, createdAt } = ugc;
    yield call(
      api.reportSpam,
      type,
      category,
      reason,
      language,
      contentId,
      digest,
      createdAt,
      baid
    );

    yield put(reportPlayerSucceeded(contentId));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server': {
        const { code } = error.response.data;
        yield put(userActions.causeServerError(code));
        break;
      }
      case 'unstable-network': {
        yield put(reportPlayerFailed({ type: 'unstable-network' }));
        break;
      }
      default:
        yield put(reportPlayerFailed({ type: 'unknown' }));
    }
  }
}

// helper function
function getFilteredArticles(articles: NewspaperArticle[], contentId: string): NewspaperArticle[] {
  const filteredArticles: NewspaperArticle[] = [];

  for (const article of articles) {
    const attributes = article.attributes;
    const filteredAttributes = attributes.filter(attr => {
      return attr.type !== 'player' || attr.contentId !== contentId;
    });
    article.attributes = filteredAttributes;
    filteredArticles.push(article);
  }

  return filteredArticles;
}

async function getNewspaperLatest(requestedAt: string, language: string, baid: string) {
  return api.getNewspaperLatest(requestedAt, language, baid).catch(err => {
    if (err.response.data?.code && err.response.data.code === '3005') {
      return undefined;
    }
    throw err;
  });
}
